import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import UsermanMenu from "../components/UsermanMenu";

const UsermanSession = () => {
  const [searchParams] = useSearchParams();
  const usermanId = searchParams.get("userman_id");
  const deviceName = searchParams.get("device_name");

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const [filters, setFilters] = useState({
    username: "",
    status: "",
  });

  const showToast = (message, type) => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(""), 3000);
  };

  const fetchSessions = async (filterParams = {}, isSync = false) => {
    setLoading(true);
    let url = "https://api.wifikamu.my.id/userman-sessions-get";
    const queryParams = new URLSearchParams();

    if (isSync) {
      queryParams.append("sync", "true");
      queryParams.append("userman_id", "1"); // Menggunakan ID placeholder, sesuaikan jika perlu
    } else {
      if (filterParams.username) {
        queryParams.append("username", filterParams.username);
      }
      if (filterParams.status) {
        queryParams.append("status", filterParams.status);
      }
    }

    url += `?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Gagal mengambil data dari API.");
      }

      const data = await response.json();
      if (data.success) {
        setSessions(data.data);
        showToast(data.message, "success");
      } else {
        throw new Error(data.message || "Gagal mengambil data.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showToast(`Error fetching data: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions(filters);
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchSessions(filters);
  };

  const handleSyncData = () => {
    fetchSessions({}, true);
  };

  const handleDelete = (session) => {
    setSessionToDelete(session);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    setIsConfirmModalOpen(false);
    try {
      const response = await fetch(
        "https://api.wifikamu.my.id/userman-sessions-delete",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username: sessionToDelete.username }),
        }
      );

      if (!response.ok) {
        throw new Error("Gagal menghapus data.");
      }
      const result = await response.json();
      if (result.success) {
        showToast(result.message, "success");
        fetchSessions(filters); // Refresh data setelah penghapusan
      } else {
        throw new Error(result.message || "Gagal menghapus sesi.");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showToast(`Error deleting data: ${error.message}`, "error");
    } finally {
      setLoading(false);
      setSessionToDelete(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="p-6 font-sans antialiased text-gray-800 dark:bg-gray-900 dark:text-gray-200 min-h-screen">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          body { font-family: 'Inter', sans-serif; }
        `}
      </style>

      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-xl shadow-lg transition-all duration-300 ease-in-out ${
            toastType === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {toastMessage}
        </div>
      )}

      {/* Main Content */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-user-check"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <polyline points="16 11 18 13 22 9" />
          </svg>
          {deviceName} /
          <UsermanMenu userman_id={usermanId} device_name={deviceName} />/
          Session
        </h1>
        <div className="flex gap-2">
          <button
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-md"
            onClick={handleSyncData}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-refresh-cw"
            >
              <path d="M21 12A9 9 0 0 0 6 21c-2.7-.7-4.5-2.5-6-4.5" />
              <path d="M3 12A9 9 0 0 1 18 3c2.7.7 4.5 2.5 6 4.5" />
              <path d="M17.5 14.5-1.5 8.5" />
              <path d="M22 12s-4 2-6 2-6-2-12-2-6 2-6 2v2s4-2 6-2 6 2 12 2 6-2 6-2Z" />
            </svg>
            Sinkronisasi
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="mb-4 p-4 rounded-xl shadow bg-white dark:bg-gray-800">
        <h2 className="text-xl font-semibold mb-2">Filter Data</h2>
        <form
          onSubmit={handleFilterSubmit}
          className="flex flex-col md:flex-row gap-4"
        >
          <input
            className="w-full md:w-1/2 border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            type="text"
            name="username"
            placeholder="Filter berdasarkan Username"
            value={filters.username}
            onChange={handleFilterChange}
          />
          <input
            className="w-full md:w-1/2 border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            type="text"
            name="status"
            placeholder="Filter berdasarkan Status"
            value={filters.status}
            onChange={handleFilterChange}
          />
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-md w-full md:w-auto"
          >
            Terapkan Filter
          </button>
        </form>
      </div>

      {/* Main Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="w-16 h-16 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl shadow bg-white dark:bg-gray-800">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="p-3 text-left font-semibold">Username</th>
                <th className="p-3 text-left font-semibold">Mikrotik ID</th>
                <th className="p-3 text-left font-semibold">IP Address</th>
                <th className="p-3 text-left font-semibold">MAC Address</th>
                <th className="p-3 text-left font-semibold">Status</th>
                <th className="p-3 text-left font-semibold">Uptime</th>
                <th className="p-3 text-left font-semibold">Mulai</th>
                <th className="p-3 text-left font-semibold">Selesai</th>
                <th className="p-3 text-left font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id} className="border-t dark:border-gray-700">
                  <td className="p-3">{session.username}</td>
                  <td className="p-3">{session.mikrotik_id}</td>
                  <td className="p-3">{session.user_address}</td>
                  <td className="p-3">{session.calling_station_id}</td>
                  <td className="p-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        session.status.includes("start")
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {session.status}
                    </span>
                  </td>
                  <td className="p-3">{session.uptime}</td>
                  <td className="p-3">{formatDate(session.started)}</td>
                  <td className="p-3">{formatDate(session.ended)}</td>
                  <td className="p-3">
                    <button
                      onClick={() => handleDelete(session)}
                      className="flex items-center gap-1 px-3 py-2 text-red-600 hover:text-red-800 rounded-md border border-red-600 hover:border-red-800 text-xs"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-trash-2"
                      >
                        <path d="M3 6h18" />
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        <line x1="10" x2="10" y1="11" y2="17" />
                        <line x1="14" x2="14" y1="11" y2="17" />
                      </svg>
                      Hapus
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation Modal */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
              Konfirmasi Hapus
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500 dark:text-gray-200">
                Apakah kamu yakin ingin menghapus sesi untuk username "
                {sessionToDelete?.username}"? Aksi ini tidak bisa dibatalkan.
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
                onClick={() => setIsConfirmModalOpen(false)}
              >
                Batal
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                onClick={confirmDelete}
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsermanSession;
